import { useQuery } from '@tanstack/react-query'
import { isDefined } from 'utils/type-guard/isDefined'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { getPollingGCTime } from '@omnea-app/frontend/utils/react-query/queryConfig'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import {
  RequestStepTaskError as ApiRequestStepTaskError,
  DetailedRequestStepTaskError as ApiDetailedRequestStepTaskError,
} from '@omnea-app/requests/requestsFrontendClient'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export const GetRequestStepTaskErrorsQueryKey = 'GetRequestStepTaskErrors'

export interface UseGetRequestStepTaskErrorsProps {
  requestId?: string
  refetchInterval?: number | false
}

export type DetailedRequestStepTaskError = Omit<ApiDetailedRequestStepTaskError, 'request_step_task'> & {
  requestStepTask: ApiDetailedRequestStepTaskError['request_step_task']
}

export type SimpleRequestStepTaskError = {
  requestStepTaskId: string
  resolvedAt: string | null
}

export type RequestStepTaskError = DetailedRequestStepTaskError | SimpleRequestStepTaskError

export const isDetailedRequestStepTaskError = (error: RequestStepTaskError): error is DetailedRequestStepTaskError =>
  'id' in error

const transformRequestStepTaskError = (error: ApiRequestStepTaskError): RequestStepTaskError => {
  if (!('id' in error)) {
    return error
  }

  return {
    ...error,
    requestStepTask: error.request_step_task,
  }
}

/**
 * Fetches all request step task errors for a request.
 *
 * Lambda returns admins full error detail, users only get resolvedAt timestamps required to detect presence of errors.
 */
export const useGetRequestStepTaskErrors = ({ requestId, refetchInterval }: UseGetRequestStepTaskErrorsProps) => {
  const { getToken } = useAuthenticatedUser()

  const result = useQuery({
    refetchInterval,
    enabled: isDefined(requestId),
    queryKey: [GetRequestStepTaskErrorsQueryKey, requestId],
    gcTime: getPollingGCTime(refetchInterval),
    queryFn: async () => {
      const apiClient = new RequestsHttpClient({ authorization: await getToken() })
      const { data, error } = await apiClient.GET('/{id}/request-step-task-errors', {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        params: { path: { id: requestId! } }, // the enabled property above ensures `requestId` will be defined
      })

      throwIfApiError(error)

      return {
        ...data,
        requestStepTaskErrors: data?.requestStepTaskErrors?.map(transformRequestStepTaskError) ?? [],
      }
    },
  })
  return result
}
